import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  JSX,
} from 'react';
import { ICancelReasonCode } from '../models/cancel-items';
import { IAvailableFulfillmentOptionsResponse } from '../models/requests/omm';
import { IOrderDetailsResponse } from '../models/order-details';
import { TOosRecoveryOption } from '../constants/out-of-stock';

export interface IOrderContext {
  currentBopisOosDrawer: TOosRecoveryOption | '';
  setCurrentBopisOosDrawer: Dispatch<SetStateAction<TOosRecoveryOption | ''>>;
  showDrawerError: number | null;
  setShowDrawerError: (value: number | null) => void;
  showExcludedShipStateApiError: Record<number, boolean>;
  setExcludedShipStateApiError: Dispatch<
    SetStateAction<Record<number, boolean>>
  >;
  showReasonCodesApiError: Record<number, boolean>;
  setReasonCodesApiError: Dispatch<SetStateAction<Record<number, boolean>>>;
  cancellationReasonCodes: ICancelReasonCode[];
  setCancellationReasonCodes: Dispatch<SetStateAction<ICancelReasonCode[]>>;
  orderDetailsData: IOrderDetailsResponse;
  setOrderDetailsData: Dispatch<SetStateAction<IOrderDetailsResponse>>;
  orderId: string | undefined;
  setOrderId: Dispatch<SetStateAction<string | undefined>>;
  customerType: string | undefined;
  setCustomerType: Dispatch<SetStateAction<string | undefined>>;
  showDeliveryAvailableSlotsApiError: Record<number, boolean>;
  setDeliveryAvailableSlotsApiError: Dispatch<
    SetStateAction<Record<number, boolean>>
  >;
  showDeliveryAddressesApiError: Record<number, boolean>;
  setDeliveryAddressesApiError: Dispatch<
    SetStateAction<Record<number, boolean>>
  >;
  fulfillmentOptionsResponse: IAvailableFulfillmentOptionsResponse | null;
  setFulfillmentOptionsResponse: Dispatch<
    SetStateAction<IAvailableFulfillmentOptionsResponse | null>
  >;
}

interface IOrderProvider {
  children: JSX.Element;
}

export const OrderContext = createContext<IOrderContext>({
  currentBopisOosDrawer: '',
  setCurrentBopisOosDrawer: () => null,
  showDrawerError: null,
  setShowDrawerError: () => null,
  showExcludedShipStateApiError: {},
  setExcludedShipStateApiError: () => null,
  showReasonCodesApiError: {},
  setReasonCodesApiError: () => null,
  cancellationReasonCodes: [],
  setCancellationReasonCodes: () => null,
  orderDetailsData: {} as IOrderDetailsResponse,
  setOrderDetailsData: () => null,
  orderId: '',
  setOrderId: () => undefined,
  customerType: undefined,
  setCustomerType: () => undefined,
  showDeliveryAvailableSlotsApiError: {},
  setDeliveryAvailableSlotsApiError: () => undefined,
  showDeliveryAddressesApiError: {},
  setDeliveryAddressesApiError: () => undefined,
  fulfillmentOptionsResponse: null,
  setFulfillmentOptionsResponse: () => null,
});

export const OrderProvider = ({ children }: IOrderProvider) => {
  const [currentBopisOosDrawer, setCurrentBopisOosDrawer] = useState<
    TOosRecoveryOption | ''
  >('');
  const [showDrawerError, setShowDrawerError] = useState<number | null>(null);
  const [showExcludedShipStateApiError, setExcludedShipStateApiError] =
    useState<Record<number, boolean>>({});
  const [showReasonCodesApiError, setReasonCodesApiError] = useState<
    Record<number, boolean>
  >({});
  const [cancellationReasonCodes, setCancellationReasonCodes] = useState<
    ICancelReasonCode[]
  >([]);
  const [
    showDeliveryAvailableSlotsApiError,
    setDeliveryAvailableSlotsApiError,
  ] = useState<Record<number, boolean>>({});
  const [orderDetailsData, setOrderDetailsData] =
    useState<IOrderDetailsResponse>({} as IOrderDetailsResponse);
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [customerType, setCustomerType] = useState<string | undefined>(
    undefined,
  );
  const [showDeliveryAddressesApiError, setDeliveryAddressesApiError] =
    useState<Record<number, boolean>>({});
  const [fulfillmentOptionsResponse, setFulfillmentOptionsResponse] =
    useState<IAvailableFulfillmentOptionsResponse | null>(null);

  return (
    <OrderContext.Provider
      value={{
        currentBopisOosDrawer,
        setCurrentBopisOosDrawer,
        showDrawerError,
        setShowDrawerError,
        showExcludedShipStateApiError,
        setExcludedShipStateApiError,
        showReasonCodesApiError,
        setReasonCodesApiError,
        cancellationReasonCodes,
        setCancellationReasonCodes,
        orderDetailsData,
        setOrderDetailsData,
        orderId,
        setOrderId,
        customerType,
        setCustomerType,
        showDeliveryAvailableSlotsApiError,
        setDeliveryAvailableSlotsApiError,
        showDeliveryAddressesApiError,
        setDeliveryAddressesApiError,
        fulfillmentOptionsResponse,
        setFulfillmentOptionsResponse,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
